import { Component } from '@theme/component';

/**
 * DAVIDsTEA corner popup: the card that invites a visitor to subscribe, or points them at a
 * promotion, on collection and product pages.
 *
 * Three things this owns and the section does not.
 *
 * When it opens. A timer, nothing else. No exit intent — it has no mobile equivalent and
 * would leave the card unreachable on the two page types this runs on. In the theme editor
 * the timer is skipped entirely: the card opens on load and re-opens when the section is
 * selected, otherwise the merchant cannot style what they cannot see.
 *
 * When it stays shut. A dismissal is written to storage under a key carrying the section id,
 * so re-adding the section starts a fresh count. `remember_days` picks the store as well as
 * the span: 0 means sessionStorage and once per visit, anything above means localStorage and
 * that many days. Every access is guarded — storage throws outright in Safari's private mode,
 * and a popup that cannot remember is better than a page that stops rendering.
 *
 * How the form is sent. Through `fetch`, not a page load, and that is the deliberate part.
 * A plain post to /contact reloads the page, which on a collection page means losing the
 * scroll position and every filter the visitor had applied. It also decouples this card from
 * the footer's newsletter form: `form.posted_successfully?` is true for every customer form
 * on the page once any one of them has posted, so a Liquid success state here would fire on
 * a footer signup too. Shopify answers a successful post with a redirect carrying
 * `customer_posted=true`, which is what the response URL is read for.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} card
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [live]
 * @property {HTMLElement} [fields]
 *
 * @extends {Component<Refs>}
 */
export class DtPopup extends Component {
  requiredRefs = ['card'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  #pending = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      this.open();
      return;
    }

    document.addEventListener('keydown', this.#handleKeydown);

    if (this.#isDismissed()) return;

    this.#openTimer = setTimeout(() => this.open(), this.#delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    super.disconnectedCallback();
  }

  /** Shows the card. Opening is idempotent — a second call while open does nothing. */
  open() {
    if (this.classList.contains('is-open')) return;
    this.classList.add('is-open');
  }

  /** Hides the card without recording anything. */
  close() {
    this.classList.remove('is-open');
  }

  /** Hides the card and remembers it, which is what the close button and Escape do. */
  dismiss() {
    this.close();
    this.#remember();
  }

  /**
   * Sends the subscription without leaving the page.
   *
   * @param {SubmitEvent} event
   */
  async subscribe(event) {
    event.preventDefault();

    // Read off the DOM rather than through a ref: `{% form %}` takes its extra parameters as
    // Liquid `key: value` pairs, and `ref` is not a legal key there.
    const form = this.querySelector('form');
    if (!form || this.#pending) return;

    this.#pending = true;
    this.classList.add('is-busy');

    let succeeded = false;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      succeeded = new URL(response.url).searchParams.get('customer_posted') === 'true';
    } catch {
      succeeded = false;
    }

    this.#pending = false;
    this.classList.remove('is-busy');
    this.#report(succeeded);

    if (succeeded) {
      // Recorded straight away rather than on the close: whichever way the card goes away,
      // someone who has just subscribed must not be asked again.
      this.#remember();
      this.#closeTimer = setTimeout(() => this.close(), 4000);
    }
  }

  /**
   * Swaps the field group for the outcome message.
   *
   * @param {boolean} succeeded
   */
  #report(succeeded) {
    const { status, live, fields } = this.refs;
    if (!status) return;

    const message = succeeded ? this.dataset.successMessage ?? '' : this.dataset.errorMessage ?? '';

    status.textContent = message;
    status.hidden = false;

    // The visible message above is `hidden` until this runs, and a hidden element is outside the
    // accessibility tree, so writing into it announces nothing. `live` is never hidden, so the
    // same text written there is what a screen reader actually hears.
    if (live) live.textContent = message;

    // A rejected address is a field error, not just a notice — say so on the input itself so it
    // is still there when someone tabs back to it.
    const input = this.querySelector('.dt-popup__input');
    if (input) {
      if (succeeded) input.removeAttribute('aria-invalid');
      else input.setAttribute('aria-invalid', 'true');
    }

    if (succeeded && fields) fields.hidden = true;
  }

  get #designMode() {
    return Boolean(window.Shopify?.designMode);
  }

  get #delay() {
    return Number(this.dataset.delay ?? 0) * 1000;
  }

  get #rememberDays() {
    return Number(this.dataset.rememberDays ?? 0);
  }

  /** localStorage past a day, sessionStorage at zero — the span picks the store. */
  get #storage() {
    try {
      return this.#rememberDays > 0 ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  }

  #isDismissed() {
    const key = this.dataset.key;
    if (!key) return false;

    try {
      const stamp = this.#storage?.getItem(key);
      if (!stamp) return false;
      if (this.#rememberDays <= 0) return true;

      return Date.now() - Number(stamp) < this.#rememberDays * 86400000;
    } catch {
      return false;
    }
  }

  #remember() {
    const key = this.dataset.key;
    if (!key || this.#designMode) return;

    try {
      this.#storage?.setItem(key, String(Date.now()));
    } catch {
      // Private browsing. The card simply comes back on the next visit.
    }
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.classList.contains('is-open')) return;

    this.dismiss();
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!this.#belongsToSection(event)) return;
    this.open();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (!this.#belongsToSection(event)) return;
    this.close();
  };

  /** @param {Event} event */
  #belongsToSection(event) {
    const target = /** @type {HTMLElement | null} */ (
      /** @type {CustomEvent} */ (event).target instanceof HTMLElement ? event.target : null
    );

    return Boolean(target?.contains(this));
  }
}

if (!customElements.get('dt-popup')) {
  customElements.define('dt-popup', DtPopup);
}
